import { useCallback } from 'react';
import { useGroupLicensePermissions } from '@group/License/useGroupLicensePermissions';
import { useActionLogSender } from '@framework/action-log';
import {
  ViewModelClipboardPayload,
  isViewModelClipboardPayload,
} from '@view-model/application/operation/ViewModelClipboardPayload';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ApplicationClipboardPayloadParser } from '@view-model/application/clipboard/ApplicationClipboardPayloadParser';
import { ViewModelClipboardOperator } from '@view-model/application/clipboard';
import { useCurrentUserId } from '@framework/auth';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { ClipboardUtils } from '@framework/ClipboardUtils';
import { setScrolledViewModelAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeControlState';
import { openFoldersAtom } from '@user/Sidebar/SidebarContainer/SidebarTree/SidebarTreeStore/sidebarTreeOpenState';
import { useSetAtom } from 'jotai';
import { copyViewModelAIAssistantSkills } from '@view-model/application/ai/ViewModelAIAssistantSkillSource';
import { Link } from 'react-router';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';

type Handler = () => Promise<void>;

/**
 * クリップボードのペイロード情報からビューモデルを作成するハンドラー関数を返すカスタムフック。
 *
 * @param groupId
 * @param workspaceId
 * @param folderId
 * @returns
 */
export const useCreateViewModelFromClipboardPayloadHandler = (
  groupId: string,
  workspaceId: string,
  folderId: string
): Handler => {
  const { t } = useTranslation();
  const actionLogSender = useActionLogSender();
  const currentUserId = useCurrentUserId();
  const canDuplicateFiles = useGroupLicensePermissions()?.fileUpload !== false;
  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);
  const openFolders = useSetAtom(openFoldersAtom);

  // 新しく作成するビューモデルの名称を組み立てる。
  // 同じワークスペース内でのコピー＆ペーストの場合にはコピー名テンプレートを適用する。
  const buildViewModelName = useCallback(
    (payload: ViewModelClipboardPayload): string => {
      return payload.sourceWorkspaceId === workspaceId
        ? t('common.label.copyName', { name: payload.name })
        : payload.name;
    },
    [t, workspaceId]
  );

  return useCallback(async (): Promise<void> => {
    if (!currentUserId) return;

    const start = new Date();
    const clipboardPayload = await ClipboardUtils.readCustomFormatJSON();
    const payload = ApplicationClipboardPayloadParser.parse(clipboardPayload);

    if (!payload || !isViewModelClipboardPayload(payload)) {
      toast.error(t('common.message.pasteFailed'));
      return;
    }

    // ファイルを複製できない場合は、空のビューモデルを残さないよう作る前に中止する
    if (
      !canDuplicateFiles &&
      ViewModelClipboardPayload.toContent(payload).getModels().fileContainerAssetIds().length > 0
    ) {
      toast.error(t('canvas.fileContainer.duplicate.licenseRequired'));
      return;
    }

    const toastId = 'view-model-pasting';

    toast(t('workspace.viewModelPaste.progress'), { id: toastId, duration: Infinity });

    const name = buildViewModelName(payload);

    // 新しくビューモデルを作る
    const viewModelId = await ViewModelOperation.create(workspaceId, folderId, name, () => void 0);

    setScrolledViewModel(viewModelId);
    openFolders({ folderIds: [folderId], workspaceId });

    // 作成したビューモデルに対してクリップボードの内容を貼り付ける
    const pasted = await ViewModelClipboardOperator.pasteWithoutCommand(groupId, viewModelId, currentUserId, payload);
    if (!pasted.ok) {
      toast.dismiss(toastId);
      toast.error(pasted.errorMessage);
      await ViewModelOperation.delete(viewModelId, () => void 0);
      return;
    }
    const { errorMessage } = pasted;

    try {
      await copyViewModelAIAssistantSkills(payload.sourceViewModelId, viewModelId);
    } catch (e) {
      console.warn('ビューモデル用AIスキルのコピーに失敗しました', e);
    }

    toast.dismiss(toastId);

    if (errorMessage) {
      toast.error(errorMessage);
    }

    // 複数のビューモデルを続けてペーストしたときや、ペースト先のフォルダを開いていないときでも
    // どのビューモデルが作られたのかを辿れるように、作成したビューモデルへのリンクにしている。
    toast(
      <Link className="underline" to={UserPagePathBuilder.viewModelPage(viewModelId)}>
        {t('workspace.viewModelPaste.success')}
      </Link>
    );

    // 行動ログを記録する
    actionLogSender('view_model:create_paste', {
      sourceViewModelId: payload.sourceViewModelId,
      viewModelId,
      viewModelName: name,
      processingTimeSec: (new Date().getTime() - start.getTime()) / 1000.0,
    });
  }, [
    actionLogSender,
    buildViewModelName,
    canDuplicateFiles,
    currentUserId,
    folderId,
    groupId,
    openFolders,
    setScrolledViewModel,
    t,
    workspaceId,
  ]);
};
